import { useState, useMemo } from "react";
import { shouldDisplay } from "@/lib/monthViewLogic";
import { type ViewingPeriod } from "@/lib/periodStorageUtils";
import {
  calculateDerivedNextPayment,
  getPeriodRange,
} from "@/lib/subscriptionDateUtils";
import {
  filterSubscriptions,
  sortSubscriptions,
  getUniqueCategories,
  type SortOption,
} from "@/lib/subscriptionFilterUtils";
import { type Subscription } from "@/types/subscription";

interface UseSubscriptionFilteringProps {
  subscriptions: Subscription[];
  currentPeriod: ViewingPeriod;
  payments: Array<{ subscription_id: string; payment_date: string }>;
  tagsBySubscriptionId: Record<string, string[]>;
  categories: Array<{ id: string; name: string }>;
}

export const useSubscriptionFiltering = ({
  subscriptions,
  currentPeriod,
  payments,
  tagsBySubscriptionId,
  categories,
}: UseSubscriptionFilteringProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("nextPayment");

  // Get subscriptions that should display in the current period
  const subscriptionsInPeriod = useMemo(() => {
    return subscriptions.filter((subscription) => {
      // Check if subscription should display based on existing logic
      if (
        shouldDisplay(subscription, currentPeriod.month, currentPeriod.year)
      ) {
        return true;
      }

      // For active/trial subscriptions, also check derived next payment
      if (subscription.status === "active" || subscription.status === "trial") {
        const derivedNext = calculateDerivedNextPayment(subscription, payments);
        const periodRange = getPeriodRange(
          currentPeriod.month,
          currentPeriod.year,
        );

        // Check if derived payment date is within the current period
        return (
          derivedNext >= periodRange.start && derivedNext <= periodRange.end
        );
      }

      return false;
    });
  }, [subscriptions, currentPeriod, payments]);

  // Apply filters and sorting
  const filteredSubscriptions = useMemo(() => {
    const filtered = filterSubscriptions(
      subscriptionsInPeriod,
      searchTerm,
      filterStatus,
      filterTags,
      tagsBySubscriptionId,
      categories,
    );

    return sortSubscriptions(filtered, sortBy);
  }, [
    subscriptionsInPeriod,
    searchTerm,
    filterStatus,
    filterTags,
    sortBy,
    tagsBySubscriptionId,
    categories,
  ]);

  // Get unique categories for filter dropdown
  const uniqueCategories = useMemo(() => {
    return getUniqueCategories(categories, tagsBySubscriptionId);
  }, [categories, tagsBySubscriptionId]);

  return {
    // State
    searchTerm,
    filterStatus,
    filterTags,
    sortBy,

    // State setters
    setSearchTerm,
    setFilterStatus,
    setFilterTags,
    setSortBy,

    // Computed values
    subscriptionsInPeriod,
    filteredSubscriptions,
    uniqueCategories,
  };
};
